import type { StoreApi } from "zustand";

import type { VideoProject } from "../../types";
import type { VideoEditorState } from "../types";
import { EMPTY_DRAFT, EMPTY_SSE, EMPTY_UI, pruneOldestDrafts } from "../utils";
import { DRAFT_MAX_ENTRIES } from "../../services/draftPersistence";

type Set = StoreApi<VideoEditorState>["setState"];

export type ProjectActions = Pick<
  VideoEditorState,
  "setCurrentProjectId" | "setProject" | "clearProject"
>;

export function createProjectActions(set: Set): ProjectActions {
  return {
    setCurrentProjectId: (id) => {
      set((state) => {
        if (state.currentProjectId === id) return state;
        const next: Partial<VideoEditorState> = { currentProjectId: id };
        if (id != null) {
          if (!state.drafts[id]) {
            next.drafts = {
              ...state.drafts,
              [id]: { ...EMPTY_DRAFT },
            };
          }
          if (!state.ui[id]) {
            next.ui = { ...state.ui, [id]: { ...EMPTY_UI } };
          }
          if (!state.sse[id]) {
            next.sse = { ...state.sse, [id]: { ...EMPTY_SSE } };
          }
        }
        return { ...state, ...next };
      });
    },
    setProject: (project: VideoProject | null, opts) => {
      set((state) => {
        if (!project) return { ...state, project: null };
        const id = project.id;
        const preserveDrafts = opts?.preserveDrafts ?? true;
        const existing = state.drafts[id];
        const drafts = preserveDrafts && existing
          ? state.drafts
          : pruneOldestDrafts(
              {
                ...state.drafts,
                [id]: { ...EMPTY_DRAFT },
              },
              DRAFT_MAX_ENTRIES,
            );
        const ui = state.ui[id]
          ? state.ui
          : { ...state.ui, [id]: { ...EMPTY_UI } };
        const sse = state.sse[id]
          ? state.sse
          : { ...state.sse, [id]: { ...EMPTY_SSE } };
        return {
          ...state,
          project,
          drafts,
          ui,
          sse,
        };
      });
    },
    clearProject: (projectId: string) => {
      set((state) => {
        if (!(projectId in state.drafts)) return state;
        const { [projectId]: _omit, ...drafts } = state.drafts;
        void _omit;
        const { [projectId]: _omitUi, ...ui } = state.ui;
        void _omitUi;
        const { [projectId]: _omitSse, ...sse } = state.sse;
        void _omitSse;
        return { ...state, drafts, ui, sse };
      });
    },
  };
}
